import {
  answersFitSideBySide,
  type CodingAnswerOption,
} from '@seen/core/codings'
import { ExtendedObservation } from '@seen/core/models/observation'
import { cn } from '@seen/ui'
import { type JSX, type ReactNode, type Ref, useState } from 'react'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { useReportPopoverOpen } from '#src/components/rich-text-editor/ObservationNode/observationFrameEngagement.ts'
import { SegmentedAnswers } from '#src/components/rich-text-editor/ObservationNode/SegmentedAnswers.tsx'
import { ValueSetAnswerPicker } from '#src/components/rich-text-editor/ObservationNode/ValueSetAnswerPicker.tsx'

export interface ObservationAnswerPickerProps {
  answerOptions: CodingAnswerOption[]
  answerValueSet: string | null
  scaleType: 'Ord' | 'Nom'
  codingDisplay?: string
  onSelect: (answer: CodingAnswerOption) => void
  // What opens the list of answers. Not shown when the answers are offered
  // side by side.
  children: ReactNode
  // Offer a few short answers side by side instead of behind `children`.
  sideBySide?: boolean
  // The recorded answer, marked as picked among side-by-side answers.
  value?: CodingAnswerOption | null
  disabled?: boolean
  // The first side-by-side answer, for moving focus to it.
  firstAnswerRef?: Ref<HTMLButtonElement>
  // Open the list straight away, for changing an answer in place.
  initialOpen?: boolean
}

// Picks a coded answer: from the node's inline list, from a value set search
// when the list was too long to carry inline, or from the default answers.
export function ObservationAnswerPicker({
  answerOptions,
  answerValueSet,
  scaleType,
  codingDisplay,
  onSelect,
  children,
  sideBySide = false,
  value,
  disabled = false,
  firstAnswerRef,
  initialOpen = false,
}: ObservationAnswerPickerProps): JSX.Element {
  const usesValueSet = !!answerValueSet && answerOptions.length === 0
  const [open, setOpenState] = useState(initialOpen)
  // The value set search reports its own popover.
  const reportOpen = useReportPopoverOpen(initialOpen && !usesValueSet)
  const setOpen = (next: boolean): void => {
    setOpenState(next)
    reportOpen(next)
  }
  if (answerValueSet && usesValueSet) {
    return (
      <ValueSetAnswerPicker
        answerValueSet={answerValueSet}
        onSelect={onSelect}
        initialOpen={initialOpen}
      >
        {children}
      </ValueSetAnswerPicker>
    )
  }

  const effectiveAnswerOptions: CodingAnswerOption[] =
    answerOptions.length > 0
      ? answerOptions
      : ExtendedObservation.getDefaultAnswerOptions(scaleType, codingDisplay)

  if (
    sideBySide &&
    answersFitSideBySide(effectiveAnswerOptions.map((option) => option.display))
  ) {
    return (
      <SegmentedAnswers
        role="radio"
        options={effectiveAnswerOptions}
        isPicked={(option) => value?.code === option.code}
        onClick={(option) => {
          if (value?.code !== option.code) {
            onSelect(option)
          }
        }}
        isDisabled={() => disabled}
        firstAnswerRef={firstAnswerRef}
      />
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-36 p-1">
        <div role="listbox" className="flex flex-col">
          {effectiveAnswerOptions.map((option) => {
            const picked = value?.code === option.code
            return (
              <button
                key={`${option.system ?? ''}|${option.code}`}
                type="button"
                role="option"
                aria-selected={picked}
                onClick={() => {
                  setOpen(false)
                  onSelect(option)
                }}
                className={cn(
                  'rounded-xs px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950',
                  picked && 'bg-accent'
                )}
              >
                {option.display}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
